/**
 * ImageLightbox — fullscreen image viewer with prev/next navigation.
 *
 * Contract (also documented in ImageLightbox.stories.jsx):
 *   • click the image     → close
 *   • click the backdrop  → close
 *   • click the X         → close
 *   • press ESC           → close
 *   • left chevron / ←    → previous image (no-op at index 0)
 *   • right chevron / →   → next image (no-op at last index)
 *   • chevrons stopPropagation so navigation does not close
 *   • single-image: chevrons hidden entirely (not just disabled)
 *   • body scroll locked while open
 *
 * Props:
 *   images        Array<{url, alt?, caption?}>
 *   initialIndex  number (default 0)
 *   onClose       () => void
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { normalizeImageUrl } from './imageUrl';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  cursor: zoom-out;
  animation: ${fadeIn} 0.15s ease-out;
`;

const ImageWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  cursor: zoom-out;
  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
    display: block;
  }
`;

const Caption = styled.figcaption`
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.95rem;
  text-align: center;
  max-width: 70ch;
  line-height: 1.5;
`;

const IconBtn = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: background 120ms ease, opacity 120ms ease;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.32);
  }
  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
`;

const PrevBtn = styled(IconBtn)`  left: 24px;  `;
const NextBtn = styled(IconBtn)`  right: 24px; `;

const CloseBtn = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }
`;

const Counter = styled.div`
  position: fixed;
  top: 24px;
  left: 24px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  z-index: 10001;
  user-select: none;
`;

export default function ImageLightbox({ images, initialIndex = 0, onClose }) {
  // Defensive last-chance normalisation — any caller that still passes a
  // raw Drive viewer URL gets corrected here before <img src> sees it.
  // Memoised so we don't re-walk the list on every re-render.
  const normalisedImages = useMemo(
    () => (images || []).map(im => ({ ...im, url: normalizeImageUrl(im.url) })),
    [images],
  );
  // Clamp the incoming index defensively — guards against bad callers and
  // makes the `useState` lazy initialiser deterministic on remount.
  const safeInitial = Math.max(0, Math.min(initialIndex, normalisedImages.length - 1));
  const [index, setIndex] = useState(safeInitial);

  const canPrev = index > 0;
  const canNext = index < normalisedImages.length - 1;
  const multiImage = normalisedImages.length > 1;

  const goPrev = useCallback((e) => {
    e?.stopPropagation();
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback((e) => {
    e?.stopPropagation();
    setIndex((i) => (i < normalisedImages.length - 1 ? i + 1 : i));
  }, [normalisedImages.length]);

  // Keyboard: ESC closes, arrow keys navigate. Listener lives on document so
  // it works even when no specific element is focused.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft' && index > 0) setIndex((i) => i - 1);
      else if (e.key === 'ArrowRight' && index < normalisedImages.length - 1) setIndex((i) => i + 1);
    };
    document.addEventListener('keydown', onKey);
    // Lock page scroll while open — restore on unmount.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, normalisedImages.length, onClose]);

  if (normalisedImages.length === 0) return null;
  const current = normalisedImages[index];

  // Clicks on the backdrop OR the image bubble up to the Backdrop's
  // onClick and close the lightbox. Buttons (chevrons + close) explicitly
  // stopPropagation so their own action runs instead of closing.
  return (
    <Backdrop onClick={() => onClose?.()}>
      {multiImage && (
        <Counter>{index + 1} / {normalisedImages.length}</Counter>
      )}

      <ImageWrap>
        <img
          src={current.url}
          alt={current.alt || ''}
          /* Deliberately no onClick — let the click bubble to the
             Backdrop so the click-to-close contract holds. */
        />
        {current.caption && (
          <Caption onClick={(e) => e.stopPropagation()}>
            {current.caption}
          </Caption>
        )}
      </ImageWrap>

      {multiImage && (
        <>
          <PrevBtn
            onClick={goPrev}
            disabled={!canPrev}
            aria-label="Edellinen kuva"
          >
            <ChevronLeft size={28} />
          </PrevBtn>
          <NextBtn
            onClick={goNext}
            disabled={!canNext}
            aria-label="Seuraava kuva"
          >
            <ChevronRight size={28} />
          </NextBtn>
        </>
      )}

      <CloseBtn
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        aria-label="Sulje kuva"
      >
        <X size={20} />
      </CloseBtn>
    </Backdrop>
  );
}
